import { Button, DrawerCard } from "@mixitone/components";
import type { TournamentMatch } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import EntryNames from "components/Pages/Tournament/EntryNames";
import { matchVoidDisplayLabel } from "tournament-state/TournamentEventTypes";
import EntryMatchesController, { type EntryMatchRow } from "./EntryMatchesController";
import { canRecordEntryMatchResult } from "./entryMatchResult";

const Statuses: Record<
  NonNullable<TournamentMatch["status"]>,
  {
    color: string;
    text: string;
  }
> = {
  completed: {
    color: "bg-green-600",
    text: "Completed",
  },
  in_progress: {
    color: "bg-blue-600",
    text: "In Progress",
  },
  ready: {
    color: "bg-yellow-400",
    text: "Ready",
  },
  pending: {
    color: "bg-gray-400",
    text: "Pending",
  },
  voided: {
    color: "bg-slate-500",
    text: "Voided",
  },
};

function statusLabel(match: TournamentMatch): string {
  if (match.isVoided || match.status === "voided") {
    return matchVoidDisplayLabel(match.voidReason);
  }
  return Statuses[match.status!]?.text || "Pending";
}

function resultLabel(result: EntryMatchRow["result"]): string | null {
  if (result === "won") return "Won";
  if (result === "lost") return "Lost";
  if (result === "voided") return null;
  return null;
}

function matchMeta(match: TournamentMatch): string {
  const parts = [`Round ${(match.round ?? 0) + 1}`];
  const group = match.format_data?.group;
  const division = match.format_data?.division;
  if (group) parts.push(String(group));
  if (division != null && division !== "") parts.push(`Div ${division}`);
  return parts.join(" · ");
}

const MatchRow = ({ row }: { row: EntryMatchRow }) => {
  const controller = EntryMatchesController.use();
  const { match, opponent, result, scoreLabel } = row;
  const status = Statuses[match.status!] || Statuses.pending;
  const outcome = resultLabel(result);
  const canScore = canRecordEntryMatchResult(row);
  const recording = !!controller.state.recordingMatchId;

  return (
    <div className="flex w-full items-center gap-3 rounded-md px-2 py-2">
      <button
        type="button"
        className="min-w-0 flex-1 rounded-md text-left hover:bg-gray-50"
        onClick={() => controller.actionOpenMatch(match)}
      >
        <div className="flex items-start gap-3">
          <div className={clsx("mt-1.5 h-2 w-2 shrink-0 rounded-full", status.color)} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-gray-900">{matchMeta(match)}</div>
            <div className="mt-0.5 truncate text-sm text-gray-700">
              {opponent ? (
                <>
                  vs <EntryNames entry={opponent} />
                </>
              ) : (
                <span className="text-gray-400">vs TBD</span>
              )}
            </div>
            {(outcome || scoreLabel || match.hasCourt) && (
              <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-500">
                {outcome && (
                  <span
                    className={clsx({
                      "text-green-700": result === "won",
                      "text-red-700": result === "lost",
                    })}
                  >
                    {outcome}
                  </span>
                )}
                {scoreLabel && <span>{scoreLabel}</span>}
                {match.hasCourt && <span>Court {match.court_id}</span>}
              </div>
            )}
          </div>
        </div>
      </button>
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-xs leading-none text-gray-500">{statusLabel(match)}</span>
        {canScore && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              kind="secondary"
              compact
              disabled={recording}
              aria-label="Won"
              data-testid={`entry-match-won-${match.id}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void controller.actionRecordResult(row, "won");
              }}
            >
              W
            </Button>
            <Button
              type="button"
              kind="secondary"
              compact
              disabled={recording}
              aria-label="Lost"
              data-testid={`entry-match-lost-${match.id}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                void controller.actionRecordResult(row, "lost");
              }}
            >
              L
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const EntryMatchesSection = ApplicationView(() => {
  const controller = EntryMatchesController.use();
  const { loading, matches } = controller.state;

  if (loading) {
    return (
      <DrawerCard title="Matches">
        <div className="text-sm text-gray-500">Loading matches…</div>
      </DrawerCard>
    );
  }

  return (
    <DrawerCard title="Matches">
      {matches.length === 0 ? (
        <div className="text-sm text-gray-500">No matches yet</div>
      ) : (
        <div className="-mx-2 divide-y divide-gray-100">
          {matches.map((row) => (
            <MatchRow key={row.match.id} row={row} />
          ))}
        </div>
      )}
    </DrawerCard>
  );
});

export default EntryMatchesController.scope(EntryMatchesSection);
